import { dependsOnConfig } from '../configs/permissions';
import type {
  AdminPermissionKey,
  AdminPermissionInherits,
  AdminPermissionsState,
} from '@/types/teams';

export const getPermissionsObject = <T extends object = AdminPermissionsState>(
  permissions: AdminPermissionKey[],
) => permissions.reduce<Partial<T>>((acc, permission) => {
  const exceptions = [
    'affiliates_sub_affiliates_max_percent',
    'offers_revshare_max_percent_sport',
    'offers_revshare_max_percent_casino',
    'offers_cpa_max_payout',
    'offers_cpa_max_payout_currency_code',
    'offers_cpa_min_payout_deposits_sum_ratio',
    'players_available_properties',
    'players_available_property_groups',
    'reports_affiliates_types',
    'reports_view_affiliates_visibility_teammates_id',
    'media_buying_reports_affiliates_types_view',
    'affiliates_types_view',
    'affiliates_types_edit',
    'affiliates_view_affiliates_visibility_teammates_id',
    'affiliates_edit_affiliates_visibility_teammates_id',

    'crm_segments_view',
    'crm_segments_review',
    'crm_segments_edit',
    'crm_activities_view',
    'crm_activities_review',
    'crm_activities_edit',
    'crm_activities_run',

    'media_buying_posts_view_affiliates_visibility_teammates_id',
    'media_buying_posts_review_affiliates_visibility_teammates_id',
    'media_buying_payments_view_affiliates_visibility_teammates_id',
    'media_buying_payments_review_affiliates_visibility_teammates_id',
    'media_buying_reports_view_affiliates_visibility_teammates_id',

    'media_buying_payments_affiliates_types_view',
    'media_buying_payments_affiliates_types_review',
    'media_buying_payments_moderation_statuses_visible',
    'media_buying_payments_payout_statuses_visible',
    'media_buying_posts_affiliates_types_view',
    'media_buying_posts_affiliates_types_review',
    'media_buying_posts_statuses_visible',
    'media_buying_reports_affiliates_types_view',
  ];

  if (!exceptions.includes(permission)) {
    acc[permission as keyof T] = 'deny' as T[keyof T];
  }

  return acc;
}, {});

export const getInheritsObject = (
  permissions?: AdminPermissionKey[],
  value = true,
): AdminPermissionInherits => permissions
  ? permissions.reduce<AdminPermissionInherits>((acc, permission) => {
    acc[permission] = value;
    return acc;
  }, {})
  : {};

export const getPermissionsSnapshot = <T extends object = AdminPermissionsState>(
  permissions: T,
  permissionsArray: AdminPermissionKey[],
) => permissionsArray.reduce<Partial<T>>((acc, permission) => {
  const key = permission as keyof T;
  const value = permissions[key];

  if (value !== undefined) {
    acc[key] = (Array.isArray(value) ? [...value] : value) as T[keyof T];
  }

  return acc;
}, {});

export const applyPermissionsSnapshot = <T extends object>(
  target: T,
  snapshot: Partial<T> = {},
) => {
  Object.entries(snapshot).forEach(([key, value]) => {
    if (value !== undefined) {
      target[key as keyof T] = value as T[keyof T];
    }
  });
};

export const sortPermissionsSnapshot = <T extends object = AdminPermissionsState>(
  permissionsSnaphot: Partial<T> = {},
  permissions: T,
) => {
  const permissionsSnaphotKeys = Object.keys(permissionsSnaphot) as AdminPermissionKey[];

  return permissionsSnaphotKeys.reduce((acc, permission) => {
    const dependsOn = dependsOnConfig[permission] ?? [];

    const computedPermissions = { ...permissions, ...acc };

    const notAllowedForDeny = dependsOn.some(
      (dependency) => computedPermissions[dependency as keyof T] === 'deny',
    );

    if (notAllowedForDeny) {
      delete acc[permission as keyof T];
    }

    return acc;
  }, permissionsSnaphot);
};

export const getInheritsSnapshot = (
  inherits: AdminPermissionInherits,
  inheritsArray: AdminPermissionKey[],
) => inheritsArray.reduce<AdminPermissionInherits>((acc, inherit) => {
  acc[inherit] = inherits[inherit];

  return acc;
}, {});

export const sortInheritsSnapshot = (
  inheritsSnaphot: AdminPermissionInherits = {},
  inherits: AdminPermissionInherits,
) => {
  const inheritsSnaphotKeys = Object.keys(inheritsSnaphot) as AdminPermissionKey[];

  return inheritsSnaphotKeys.reduce((acc, inherit) => {
    const dependsOn = dependsOnConfig[inherit] ?? [];

    const computedInherits = { ...inherits, ...acc };

    const notAllowedForDeny = dependsOn.some((dependency) => computedInherits[dependency] === true);

    if (notAllowedForDeny) {
      delete acc[inherit];
    }

    return acc;
  }, inheritsSnaphot);
};
